<template>
    <div class="d-flex flex-fill flex-column" v-if="loaded">
        <div>
            <fe-label>
                Periods
                <span v-if="periods.filter(r=>r.require_flag).length" style="margin-left: 20px; font-size: 10px; font-weight: bold;">*Required Periods added</span>
            </fe-label>
            <fe-remote-combo
                data-test="periods-expectations-periods"
                :items="periods.filter(r=>!r.require_flag)"
                itemText="monitor_period_name"
                itemValue="id"
                v-model="selectedPeriods"
                multiple
                moreText
                :disabled="disabled"
                @change="resetPeriods"
                :rules="$fieldValidators('select', 'Selection', { required: true, disabled })"
            />
        </div>

        <div>
            <fe-label>Expectations</fe-label>
            <fe-remote-combo
                data-test="periods-expectations-expectations"
                :items="expectations"
                itemText="expectation_name"
                itemValue="id"
                v-model="selectedExpectations"
                multiple
                moreText
                :disabled="disabled"
                @change="resetExpectations"
                :rules="$fieldValidators('select', 'Selection', { required: true, disabled })"
            />
        </div>

        <v-input v-model="selectedPeriods" dense :rules="disabled ? [] : [validatePeriods]" hide-details></v-input>

        <div v-if="selectedPeriods.length" class="caption">Drag and drop to reorder</div>
        <v-expansion-panels v-if="selectedPeriods.length" flat>
            <draggable style="width: 100%;" v-model="selectedPeriods" :disabled="disabled" data-test="periods-expectations-periods-draggable" aria-label="Reorder periods">
                <v-expansion-panel v-for="(p, i) in selectedPeriods" class="mb-2 point-sheet-editor">
                    <v-expansion-panel-header class="selections-header" :aria-label="selectedPeriodHasErrors[p.monitor_period_id] ? `${selectedPeriods[i].monitor_period_name} period, has score errors` : `${selectedPeriods[i].monitor_period_name} period`">
                        <div>
                            <v-tooltip bottom v-if="selectedPeriodHasErrors[p.monitor_period_id]">
                                <template #activator="{on}">
                                    <v-icon v-on="on" aria-label="Please review the scores for this period" style="font-size: 12px; color: #F02D1F;">fas fa-exclamation-triangle</v-icon>
                                </template>
                                Please review the scores for this period
                            </v-tooltip>
                            {{selectedPeriods[i].monitor_period_name}}
                        </div>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content class="pa-0">
                        <v-data-table :items-per-page="100" :headers="headers" :items="selectedPeriods[i].expectations" style="border: 1px solid #e6e6e6; border-radius: 0px 0px 5px 5px;">
                            <template v-slot:item.no_score_flag="{ item }">
                                <v-simple-checkbox v-model="item.no_score_flag" :disabled="disabled" @input="changeScore" data-test="periods-expectations-no-score-checkbox" :aria-label="`Not scored for ${item.expectation_name}`"></v-simple-checkbox>
                            </template>
                            <!-- lowest score editor -->
                            <template v-slot:item.lowest_score="props">
                                <span v-if="disabled">{{props.item.no_score_flag ? '--' : props.item.lowest_score}}</span>
                                <v-edit-dialog v-else>
                                    <v-icon v-if="props.item.force_score_range" aria-label="Score range is locked">fas fa-lock</v-icon>
                                    <v-tooltip bottom v-if="!props.item.no_score_flag && minScoreErrors[p.monitor_period_id][props.item.sub_category_id]">
                                        <template #activator="{on}">
                                            <v-icon v-on="on" :aria-label="minScoreErrors[p.monitor_period_id][props.item.sub_category_id]" style="font-size: 12px; color: #F02D1F;">fas fa-exclamation-triangle</v-icon>
                                        </template>
                                        {{ minScoreErrors[p.monitor_period_id][props.item.sub_category_id] }}
                                    </v-tooltip>
                                    {{props.item.no_score_flag ? '--' : props.item.lowest_score}}
                                    <template #input v-if="!props.item.force_score_range">
                                        <v-text-field
                                            data-test="periods-expectations-lowest-score-input"
                                            class="mt-2"
                                            flat solo dense type="number" step="1" required
                                            single-line
                                            :readonly="props.item.no_score_flag === 1 ? true : false"
                                            v-model="props.item.lowest_score"
                                            @change="changeScore"
                                        />
                                    </template>
                                </v-edit-dialog>
                            </template>

                            <!-- highest score editor -->
                            <template v-slot:item.highest_score="props">
                                <span v-if="disabled">{{props.item.no_score_flag ? '--' : props.item.highest_score}}</span>
                                <v-edit-dialog v-else>
                                    <v-icon v-if="props.item.force_score_range" aria-label="Score range is locked">fas fa-lock</v-icon>
                                    <v-tooltip bottom v-if="maxScoreErrors[p.monitor_period_id][props.item.sub_category_id]">
                                        <template #activator="{on}">
                                            <v-icon v-on="on" :aria-label="maxScoreErrors[p.monitor_period_id][props.item.sub_category_id]" style="font-size: 12px; color: #F02D1F;">fas fa-exclamation-triangle</v-icon>
                                        </template>
                                        {{ maxScoreErrors[p.monitor_period_id][props.item.sub_category_id] }}
                                    </v-tooltip>

                                    {{props.item.no_score_flag ? '--' : props.item.highest_score}}
                                    <template #input v-if="!props.item.force_score_range">
                                        <v-text-field
                                            data-test="periods-expectations-highest-score-input"
                                            class="mt-2"
                                            flat solo dense type="number"
                                            single-line
                                            :readonly="props.item.no_score_flag === 1 || props.item.force_score_range === 1"
                                            v-model="props.item.highest_score"
                                            @change="changeScore"
                                        />
                                    </template>
                                </v-edit-dialog>
                            </template>

                        </v-data-table>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </draggable>
        </v-expansion-panels>
    </div>
</template>

<script>
    import Draggable from 'vuedraggable';

    export default {
        name: 'PeriodsExpectations',
        components: { Draggable },
        props: {
            value: {
                type: Object,
                default: () => {
                    return {
                        monitors: [],
                    }
                }
            },
            strategy: {
                type: Object,
                default: null,
            },
            disabled: {
                type: Boolean,
                default: false
            }
        },
        watch: {
            localValue(v) {
                this.$emit('input', v)
            },
            selectedPeriods(v) {
                this.$emit('selectedPeriods', v)
                this.setTableData()
                this.formatData()
            },
            'strategy.id'(newId, oldId) {
                if (newId && newId !== oldId) {
                    this.loaded = false
                    this.loadData()
                }
            }
        },
        data() {
            return {
                loaded: false,
                headers: [{
                    value: 'expectation_name',
                    align: 'start',
                    sortable: false
                }, {
                    text: 'Not Scored',
                    value: 'no_score_flag',
                    sortable: false,
                    align: 'center'
                }, {
                    text: 'Min Score',
                    value: 'lowest_score',
                    sortable: false,
                    align: 'end'
                }, {
                    text: 'Max Score',
                    value: 'highest_score',
                    sortable: false,
                    align: 'end'
                }],
                localValue: {monitors: []},
                expectations: [],
                periods: [],
                selectedPeriods: [],
                selectedExpectations: [],
                selectedPeriodHasErrors: {},
                minScoreErrors: {},
                maxScoreErrors: {},
            }
        },
        created() {
            if (this.value) {
                this.localValue = this.$_.assign(this.localValue, this.value)
            }
            this.loadData()
        },
        methods: {
            validatePeriods(v) {
                let minScoreErrors = {}
                let maxScoreErrors = {}
                let selectedPeriodHasErrors = {}
                let hasError = false
                v.forEach(p => {
                    minScoreErrors[p.monitor_period_id] = {}
                    maxScoreErrors[p.monitor_period_id] = {}
                    selectedPeriodHasErrors[p.monitor_period_id] = p.expectations.some(e => {
                        let expectationHasError = false
                        if (e.no_score_flag) {
                            return false
                        }
                        if (!('' + e.lowest_score).match(/^\d+$/)) {
                            minScoreErrors[p.monitor_period_id][e.sub_category_id] = "Min score must be a whole number"
                            expectationHasError = true
                        }
                        if (!('' + e.highest_score).match(/^\d+$/)) {
                            maxScoreErrors[p.monitor_period_id][e.sub_category_id] = "Max score must be a whole number"
                            expectationHasError = true
                        }
                        if (!hasError && parseInt(e.highest_score, 10) < parseInt(e.lowest_score, 10)) {
                            maxScoreErrors[p.monitor_period_id][e.sub_category_id] = "Max score must be >= min score"
                            expectationHasError = true
                        }
                        return expectationHasError
                    })
                    hasError = hasError || selectedPeriodHasErrors[p.monitor_period_id]
                })
                this.minScoreErrors = minScoreErrors
                this.maxScoreErrors = maxScoreErrors
                this.selectedPeriodHasErrors = selectedPeriodHasErrors
                return hasError ? 'One or more periods have invalid scores' : true
            },
            changeScore(v) {
                this.formatData()
                this.validatePeriods(this.selectedPeriods)
            },
            formatData() {
                let monitors = []
                this.selectedPeriods.forEach(p => {
                    p.expectations?.forEach(e => {
                        monitors.push({
                            sub_category_id: e.sub_category_id,
                            expectation_name: e.expectation_name,
                            lowest_score: e.lowest_score,
                            highest_score: e.highest_score,
                            monitor_period_id: p.monitor_period_id,
                            force_score_range: e.force_score_range,
                            no_score_flag: e.no_score_flag ? true : false,
                        })
                    })
                })
                this.localValue = {monitors}
            },
            resetExpectations(v) {
                if (v.length === 0) this.selectedExpectations = this.expectations.filter(r => r.require_flag)
                this.$emit('expectation-change', v)
                this.$emit('selectedExpectations', v)
                this.setTableData()
                this.formatData()
            },
            resetPeriods(v) {
                if (v.length === 0) this.selectedPeriods = this.periods.filter(r => r.require_flag)
                this.$emit('period-change', v)
                this.setTableData()
                this.formatData()
            },
            setTableData() {
                if (!this.loaded) {
                    return
                }
                const previousValues = new Map()
                this.localValue.monitors?.forEach(mon => {
                    previousValues.set(mon.sub_category_id + '-' + mon.monitor_period_id, mon)
                })
                this.selectedPeriods.forEach(p => {
                    p.expectations = []
                    this.selectedExpectations.forEach(e => {
                        const previous = previousValues.get(e.sub_category_id + '-' + p.monitor_period_id) || {}
                        const mon = {
                            sub_category_id: e.sub_category_id,
                            no_score_flag: false,
                            force_score_range: e.force_score_range,
                            expectation_name: e.expectation_name,
                            lowest_score: e.lowest_score,
                            highest_score: e.highest_score
                        }
                        if (!mon.force_score_range && previous.hasOwnProperty('lowest_score')) {
                            mon.lowest_score = previous.lowest_score
                        }
                        if (!mon.force_score_range && previous.hasOwnProperty('highest_score')) {
                            mon.highest_score = previous.highest_score
                        }
                        if (previous.no_score_flag) {
                            mon.no_score_flag = previous.no_score_flag
                        }
                        p.expectations.push(mon)
                    })
                })
            },
            loadData() {
                if (!this.strategy?.id) {
                    return
                }
                Promise.all([
                    this.$modelGet('interventionTypeMonitor', { intervention_type_id: this.strategy.id }).then(expectations => {
                        this.expectations = expectations
                        this.expectations.forEach(r => { if (r.require_flag) r.disabled = true })
                    }),
                    this.$modelGet('interventionTypePeriod', { intervention_type_id: this.strategy.id }).then(periods => {
                        this.periods = periods
                        this.periods.forEach(r => { if (r.require_flag) r.disabled = true })
                    })
                ]).then(() => {
                    if (!this.localValue.monitors) {
                        this.selectedExpectations = this.expectations.filter(r => r.require_flag)
                        this.selectedPeriods = this.periods.filter(r => r.require_flag)
                    } else {
                        const selectedExpectionIdSet = new Set(this.localValue.monitors.map(mon => mon.sub_category_id))
                        const expectationMap = new Map(this.expectations.map(e => [e.sub_category_id, e]))
                        this.selectedExpectations = [
                            // sets are ordered, so return in the order the user selected (with duplicates removed)
                            ...selectedExpectionIdSet.intersection(expectationMap).values().map(id => expectationMap.get(id)),
                            // add any required expectations the user didn't already have
                            ...this.expectations.filter(e => e.require_flag && !selectedExpectionIdSet.has(e.sub_category_id))
                        ]

                        const selectedPeriodIdSet = new Set(this.localValue.monitors.map(mon => mon.monitor_period_id))
                        const periodMap = new Map(this.periods.map(p => [p.monitor_period_id, p]))
                        this.selectedPeriods = [
                            // sets are ordered, so return in the order the user selected (with duplicates removed)
                            ...selectedPeriodIdSet.intersection(periodMap).values().map(id => periodMap.get(id)),
                            // add any required periods the user didn't already have
                            ...this.periods.filter(p => p.require_flag && !selectedPeriodIdSet.has(p.monitor_period_id))
                        ]
                    }
                    this.loaded = true
                    this.$emit('loaded')

                    this.setTableData()
                })
            },
        }
    }
</script>

<style lang="scss" scoped>
.selections-header {
    background: var(--fe-secondary);
    color: white;
    border-radius: 5px 5px 0px 0px;
    height: 35px;
    line-height: 35px;
    padding-left: 16px;

    ::v-deep .v-data-footer {
        display: none !important;
    }

    ::v-deep .v-icon {
        color: white !important;
    }
}

.point-sheet-editor {
    ::v-deep .v-expansion-panel-content__wrap {
        padding: 0px;
    }

    ::v-deep .v-simple-checkbox .v-icon {
        color: var(--v-primary-base) !important;
    }

    ::v-deep .v-data-footer {
        display: none !important;
    }
}

</style>
